import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'commons/components/Button';

import ConfigurationContext from 'UserRegistration/contexts/ConfigurationContext';
import DataContext from 'UserRegistration/contexts/DataContext';
import NumberedSectionTitle from 'UserRegistration/components/NumberedSectionTitle';
import { getTranslationSuffix } from 'UserRegistration/resources/utils/translationUtils';
import Recaptcha from 'react-google-recaptcha';


function RegistrationSection(props) {

    const { componentConfig } = useContext(ConfigurationContext);
    const { registrationData, setRegistrationData } = useContext(DataContext);
    const { t } = useTranslation(componentConfig.i18nNamespaces);
    const translationContextSuffix = getTranslationSuffix(componentConfig.context);
    const captchaRef = useRef(null);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    const updateRecaptchaToken = (token) => {
        let registrationDataCopy = { ...registrationData, recaptchaToken: token };
        setRegistrationData(registrationDataCopy);
    };

    const handleButtonClick = (e) => {
        setIsButtonDisabled(true);

        registrationData.submittedRecaptchaToken = registrationData.recaptchaToken;

        props.saveButtonOnClick(e);

        if (captchaRef.current) {
            updateRecaptchaToken(null);
            captchaRef.current.reset();
        }

        // Re-enable the button after 3 second in case there is a network throttling
        setTimeout(() => {
            setIsButtonDisabled(false);
        }, 3000);
    };

    return (
        <section
            className={classnames(props.className, 'section-' + props.sectionNumber, { blocked: registrationData.metadata.currentStep < props.sectionNumber })}
            data-testid={props.id}
            id={props.id}
        >

            <NumberedSectionTitle currentSection={registrationData.metadata.currentStep} sectionNumber={props.sectionNumber} title={props.title}>
                {props.titleExtraContent}
            </NumberedSectionTitle>

            {props.children}

            {/* Button is hidden when the user has passed the section or if this is the last active section */}
            {((props.sectionNumber >= registrationData.metadata.currentStep) || (props.sectionNumber === componentConfig.activeComponents.length)) &&
                <>

                    { componentConfig.context == 'PUBLIC_REGISTRATION' && props.sectionNumber === componentConfig.activeComponents.length 
                        && registrationData.metadata.formSubmissionFailures > 0 &&
                      
                        <>
                            <Recaptcha sitekey={process.env.RECAPTCHA_SITE_KEY}
                                ref={captchaRef}
                                onChange={(t) => updateRecaptchaToken(t)}
                                onExpired={() => updateRecaptchaToken(null)} />
                            <div className="captcha-spacer"></div>
                        </>
                    }

                    <Button
                        disabled={isButtonDisabled || props.saveButtonDisabled || (captchaRef.current && !registrationData.recaptchaToken)}
                        id={`section${props.sectionNumber}SubmitButton`}
                        isPrimary={true}
                        label={props.sectionNumber === componentConfig.activeComponents.length
                            ? t('finalizeRegistration' + translationContextSuffix) : t('saveAndContinue')
                        }
                        onClick={handleButtonClick}
                    />
                </>
            }
        </section>
    );
}

RegistrationSection.propTypes = {
    /** The child elements of the parent component. */
    children: PropTypes.node,
    /** CSS className. */
    className: PropTypes.string,
    /** ID of the wrapper. Also used for the data-testid. */
    id: PropTypes.string,
    /** Whether or not the section save button is disabled. */
    saveButtonDisabled: PropTypes.bool,
    /** The action of the save button on click. */
    saveButtonOnClick: PropTypes.func,
    /** The section number of the parent component section within the entire page. */
    sectionNumber: PropTypes.number,
    /** The title of the section. */
    title: PropTypes.string,
    /** Additional content that will be displayed alongside the title. */
    titleExtraContent: PropTypes.node
};

export default RegistrationSection;